import { AlarmProfile } from "@src/cloud-connect/alarms/entities/alarm-profile";
import { fetchNodesTimeseriesDataForAlarmPanel } from "@src/cloud-connect/api/timeseries/fetch-nodes-timeseries-data-for-alarm-panel";
import { TimePeriod } from "@src/cloud-connect/blueprint/dashboards/entities/time-period";
import { RootState } from "@src/reducers/store";
import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { buildGraphOptionObjectAlarmPanel } from "./build-raw-data-graph-option-object-alarm-panel";
import { AlarmList } from "@src/cloud-connect/alarms/entities/alarm-list";
import { getAlarmProfiles } from "@src/cloud-connect/dependency-injection-container/get-alarm-profiles";
import { mergeArrays } from "@src/cloud-connect/utils/merge-arrays";

export const ALARM_NODE_DETAILS_PANEL_TAB_VALUES = {
  LAST_8_HOURS: "last8Hours",
  LAST_DAY: "lastDay",
  LAST_WEEK: "lastWeek",
  LAST_MONTH: "lastMonth",
} as const;

export type AlarmNodeDetailsPanelTabValue =
  (typeof ALARM_NODE_DETAILS_PANEL_TAB_VALUES)[keyof typeof ALARM_NODE_DETAILS_PANEL_TAB_VALUES];

export const useAlarmNodeDetailsPanelHook = (alarmListInstance: AlarmList) => {
  const [activeProfile, setActiveProfile] = useState<AlarmProfile>();
  const sourceId = useSelector(
    (state: RootState) => state.yourSlice.connectionView.source.source_id,
  );

  const alarmNodeData = useSelector(
    (state: RootState) => state.yourSlice.alarmNodeSelection.nodeData,
  );

  const { nodeId, name, unit, dataType } = alarmNodeData;

  const [graphOptionsObject, setGraphOptionsObject] = useState(undefined);
  const [tabValue, setTabValue] = useState<AlarmNodeDetailsPanelTabValue>(
    ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_8_HOURS,
  );
  const [stringDataAndTimestampData, setStringDataAndTimestampData] =
    useState(undefined);

  const buildTimePeriod = (tabValue: AlarmNodeDetailsPanelTabValue) => {
    switch (tabValue) {
      case ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_8_HOURS:
        return new TimePeriod(8, "hours");
      case ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_DAY:
        return new TimePeriod(24, "hours");
      case ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_WEEK:
        return new TimePeriod(7, "days");
      case ALARM_NODE_DETAILS_PANEL_TAB_VALUES.LAST_MONTH:
        return new TimePeriod(4, "weeks");
      default:
        return new TimePeriod(8, "hours");
    }
  };

  const getTimeseriesRawDataForAlarmPanel = useCallback(
    async (sourceId: string, timePeriod: TimePeriod, nodeId: string) => {
      const graphData = await fetchNodesTimeseriesDataForAlarmPanel(
        sourceId,
        timePeriod,
        nodeId,
        dataType,
      );

      setStringDataAndTimestampData(null);

      if (
        graphData &&
        graphData.valuesArray &&
        graphData.valuesArray.length !== 0 &&
        graphData.dataType === "numeric"
      ) {
        setGraphOptionsObject(
          buildGraphOptionObjectAlarmPanel(
            alarmNodeData.name,
            graphData.valuesArray,
            graphData.timesArray,
            unit,
            alarmListInstance.getAlarmRowByAlarmId(alarmNodeData.nodeId),
          ),
        );
      } else if (
        graphData &&
        graphData.valuesArray &&
        graphData.valuesArray.length !== 0 &&
        graphData.dataType === "string"
      ) {
        // temporary fix: since limit for the request is 50000, for strings we are
        // cutting off all elements after the 2000th to avoid DOM overload
        // https://linear.app/tomorrowthings/issue/TO-1864/bug-latest-value-not-displayed-in-fe-tree-structure-side-panel#comment-966ed98a
        setStringDataAndTimestampData(
          mergeArrays(
            graphData.timesArray.reverse().slice(0, 2000),
            graphData.valuesArray.reverse().slice(0, 2000),
          ),
        );
      } else {
        setGraphOptionsObject(null);
      }
    },
    [alarmListInstance, alarmNodeData.nodeId, unit],
  );

  useEffect(() => {
    setGraphOptionsObject(undefined);
    const alarm = alarmListInstance
      .getAlarmRowByAlarmId(alarmNodeData.nodeId)
      .getAlarm();

    const criteria = alarm.getCriteria();

    setActiveProfile(getAlarmProfiles().getActiveAlarmProfile());

    const handleAlarmInformationChange = () => {
      const timePeriod = buildTimePeriod(tabValue);
      getTimeseriesRawDataForAlarmPanel(sourceId, timePeriod, nodeId);
    };
    criteria.addObserver(handleAlarmInformationChange);

    alarm.addObserver(handleAlarmInformationChange);

    handleAlarmInformationChange();

    return () => {
      criteria.removeObserver(handleAlarmInformationChange);
      alarm.removeObserver(handleAlarmInformationChange);
    };
  }, [
    activeProfile,
    alarmListInstance,
    alarmNodeData.nodeId,
    getTimeseriesRawDataForAlarmPanel,
    nodeId,
    sourceId,
    tabValue,
  ]);

  return {
    activeProfile,
    graphOptionsObject,
    name,
    tabValue,
    setTabValue,
    stringDataAndTimestampData,
  };
};
